import {
	createContext,
	useContext,
	useEffect,
	useState,
	ReactNode,
} from 'react';
import httpClient from '@/api/httpClient';
import { useContent } from '@/providers/ContentProvider';
import { Language } from '@/types/types';
import APP_CONFIG from '@/configs/appConfig';
import { useTypedDispatch } from '@/store/hooks';
import { creatorActions } from '@/store/slices/creatorSlice';

type EmbedConfig = {
	id: string;
	companyName: string;
	logoUrl: string | null;
	primaryColor: string | null;
	defaultLanguage: string;
	quoteConsentText: string | null;
};

type PartnerContextValue = {
	embed: EmbedConfig | null;
	embedReady: boolean;
};

const PartnerContext = createContext<PartnerContextValue>({
	embed: null,
	embedReady: false,
});

export const usePartner = () => useContext(PartnerContext);

export const PartnerProvider = ({ children }: { children: ReactNode }) => {
	const dispatch = useTypedDispatch();
	const { setLanguage } = useContent();
	const [embed, setEmbed] = useState<EmbedConfig | null>(null);
	const [embedReady, setEmbedReady] = useState(() => {
		const params = new URLSearchParams(window.location.search);
		return !params.get(APP_CONFIG.PARTNER_ID_PARAM);
	});

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const partnerId = params.get(APP_CONFIG.PARTNER_ID_PARAM);
		if (partnerId) {
			httpClient.defaults.headers.common[APP_CONFIG.PARTNER_ID_HEADER] =
				partnerId;
		}
		if (!partnerId) return;
		httpClient
			.get<EmbedConfig>('partners/embed-config', { params: { partnerId } })
			.then((res) => {
				setEmbed(res.data);
				setLanguage(res.data.defaultLanguage as Language);
				dispatch(
					creatorActions.updateContact({
						companyName: res.data.companyName,
					})
				);
				setEmbedReady(true);
			})
			.catch(() => {
				setEmbedReady(true);
			});
	}, [dispatch, setLanguage]);

	return (
		<PartnerContext.Provider value={{ embed, embedReady }}>
			{children}
		</PartnerContext.Provider>
	);
};
